import type { ContractDocumentViewProps } from '@/components/contracts/ContractDocumentView';
import { asArray, asRecord, asText as narrativeText } from '@/lib/as-array';
import {
  buildCsfBlocks,
  projectDetailRowsFromBlocks,
  type CsfBlock,
} from '@/lib/contract-csf';
import { formatPaperDetailRow, formatPaperProjectId } from '@/lib/paper-display';

export type ContractAllowance = {
  type?: string | null;
  description?: string | null;
  amount?: number | string | null;
};

type AddressLike = {
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  zipCode?: string | null;
  county?: string | null;
  jurisdiction?: string | null;
};

function text(value: unknown): string | null {
  if (value == null) return null;
  const next = String(value).trim();
  return next.length ? next : null;
}

export function formatContractDate(value: unknown): string | null {
  if (value == null || value === '') return null;
  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatAddress(parts: Array<string | null | undefined>): string | null {
  const cleaned = parts.map((part) => part?.trim()).filter(Boolean) as string[];
  return cleaned.length ? cleaned.join(', ') : null;
}

/** Object or JSON-encoded jobsite blob (some seed/public payloads store a string). */
export function jobsiteFromUnknown(raw: unknown): AddressLike | null {
  return asRecord(raw) as AddressLike | null;
}

export function formatJobsiteAddress(
  jobsite?: AddressLike | null,
  project?: Record<string, unknown> | null,
): string | null {
  return formatAddress([
    text(jobsite?.addressLine1) || text(project?.jobsiteAddressLine1),
    text(jobsite?.addressLine2) || text(project?.jobsiteAddressLine2),
    [
      text(jobsite?.city) || text(project?.jobsiteCity),
      text(jobsite?.state) || text(project?.jobsiteState),
      text(jobsite?.postalCode) ||
        text(jobsite?.zipCode) ||
        text(project?.jobsitePostalCode),
    ]
      .filter(Boolean)
      .join(', '),
  ]);
}

/** Project ID on the paper (P26-xxxx). Never invents a number. */
export function resolveProjectDocumentId(input: {
  fieldCode?: unknown;
  projectNumber?: unknown;
  projectDocumentId?: unknown;
  projectLabel?: unknown;
  contractId?: unknown;
}): string {
  return formatPaperProjectId({
    fieldCode: text(input.fieldCode),
    projectNumber: text(input.projectNumber),
    projectDocumentId: text(input.projectDocumentId),
    projectLabel: text(input.projectLabel),
    contractId: text(input.contractId),
  });
}

export function premisesJurisdictionFromJobsite(jobsite?: AddressLike | null): string | null {
  if (!jobsite) return null;
  const county = text(jobsite.county) || text(jobsite.jurisdiction);
  const city = text(jobsite.city);
  const state = text(jobsite.state);
  if (county && state) return `${county}, ${state}`;
  if (city && state) return `${city}, ${state}`;
  return county || city || state;
}

const PERMIT_MUNICIPALITY_LABELS = new Set([
  'permit issuing municipality',
  'permit municipality',
  'municipality issuing the permit',
]);

function municipalityFromDetailRows(rows: Array<[string, string]>): string | null {
  for (const [label, value] of rows) {
    if (PERMIT_MUNICIPALITY_LABELS.has(label.trim().toLowerCase())) {
      return text(value);
    }
  }
  return null;
}

/** County/city from Settings municipality, then jobsite. */
export function resolvePremisesJurisdiction(input: {
  municipality?: unknown;
  permitMunicipality?: unknown;
  frozen?: unknown;
  jobsite?: AddressLike | null;
}): string | null {
  return (
    text(input.permitMunicipality) ||
    text(input.municipality) ||
    text(input.frozen) ||
    premisesJurisdictionFromJobsite(input.jobsite)
  );
}

export function defaultJurisdictionPreamble(input: {
  contractorName?: string | null;
  premisesJurisdiction?: string | null;
}): string {
  const contractor = text(input.contractorName) || 'Contractor';
  const place = text(input.premisesJurisdiction);
  const where = place ? ` and in ${place}` : '';
  return (
    `This agreement is made effective as of the “Effective Date” noted above${where} ` +
    `by and between the undersigned owners/customers (single or collectively “Customer”) ` +
    `and ${contractor} (“Contractor”) (“Contract”). For good and valuable consideration, ` +
    `the receipt and sufficiency of which is hereby acknowledged, the Customer and ` +
    `Contractor hereby agree as follows: The work called for herein (“Scope of Work”) ` +
    `will be performed at the address below (“Premises”) unless otherwise noted and ` +
    `according to the terms and specifications as outlined below.`
  );
}

function licenseFromProfile(profile: any): {
  licenseNumber: string | null;
  licenseClassification: string | null;
  licenseIssuingState: string | null;
} {
  const licenses = asArray(profile?.licenses);
  const numbers = licenses.map((row: any) => text(row?.licenseNumber)).filter(Boolean);
  const classes = licenses.map((row: any) => text(row?.licenseClassification)).filter(Boolean);
  const states = licenses
    .map((row: any) => text(row?.licenseLocation) || text(row?.issuingState))
    .filter(Boolean);
  return {
    licenseNumber: numbers.join('; ') || text(profile?.licenseNumber),
    licenseClassification: classes.join('; ') || text(profile?.licenseClassification),
    licenseIssuingState: states.join('; ') || text(profile?.licenseLocation) || text(profile?.state),
  };
}

/** Same 20 / 47 / 23 / 10 template the estimate shows when none is saved. */
export const DEFAULT_PAYMENT_MILESTONES = [
  { payment: 'Down Payment', milestone: 'Due at signing', pct: 0.2 },
  { payment: 'Start Payment', milestone: 'Due at Start of Project', pct: 0.47 },
  { payment: 'Progress Payment', milestone: 'Due at Framing Inspection', pct: 0.23 },
  { payment: 'Final Payment', milestone: 'Due at Final Inspection', pct: 0.1 },
] as const;

export type NormalizedPaymentItem = {
  label: string;
  detail: string | null;
  amount: number | null;
};

function asMoneyNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function amountFromPct(pct: unknown, contractValue: number | null): number | null {
  const fraction = asMoneyNumber(pct);
  if (fraction == null || contractValue == null) return null;
  const ratio = fraction > 1 ? fraction / 100 : fraction;
  return Math.round(contractValue * ratio * 100) / 100;
}

function firstNonEmptyList(...candidates: unknown[]): unknown[] {
  for (const candidate of candidates) {
    const rows = asArray(candidate);
    if (rows.length > 0) return rows;
  }
  return [];
}

function sumAllowanceAmounts(raw: unknown): number | null {
  const rows = asArray(raw);
  if (rows.length === 0) return null;
  let total = 0;
  let sawAmount = false;
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const amount = asMoneyNumber((row as { amount?: unknown }).amount);
    if (amount == null) continue;
    total += amount;
    sawAmount = true;
  }
  return sawAmount ? total : null;
}

export function paymentDetailsSource(input: unknown): unknown {
  const root = asRecord(input) ?? {};
  const data = asRecord(root.contractData) ?? root;
  const display = asRecord(data.display) ?? asRecord(root.overview) ?? {};
  const proposal = asRecord(data.proposal) ?? {};
  const project = asRecord(data.project) ?? asRecord(root.project) ?? {};
  return firstNonEmptyList(
    proposal.paymentDetails,
    data.paymentDetails,
    display.paymentDetails,
    project.paymentMilestones,
    root.paymentDetails,
    root.paymentMilestones,
  );
}

export function normalizePaymentItems(
  raw: unknown,
  contractValue: number | null,
): NormalizedPaymentItem[] {
  const source = asArray(raw);
  const rows = source.length > 0 ? source : [...DEFAULT_PAYMENT_MILESTONES];
  return rows.map((row) => {
    if (!row || typeof row !== 'object') {
      return { label: 'Payment', detail: null, amount: null };
    }
    const item = row as Record<string, unknown>;
    const paymentName = text(item.payment) || text(item.paymentType) || text(item.label);
    const milestoneName = text(item.milestone) || text(item.milestones);
    const label = paymentName || milestoneName || 'Payment';
    const detail =
      paymentName && milestoneName
        ? milestoneName
        : text(item.detail) || text(item.description);
    return {
      label,
      detail,
      amount:
        asMoneyNumber(item.amount) ??
        amountFromPct(item.pct ?? item.percent ?? item.percentage, contractValue),
    };
  });
}

function normalizeAllowances(raw: unknown): ContractAllowance[] {
  const rows: ContractAllowance[] = [];
  for (const row of asArray(raw)) {
    if (!row || typeof row !== 'object') continue;
    const item = row as Record<string, unknown>;
    const type = text(item.type) || text(item.label);
    const description = text(item.description) || text(item.details);
    const amount = (item.amount ?? item.value) as number | string | null;
    if (!type && !description && amount == null) continue;
    rows.push({ type, description, amount });
  }
  return rows;
}

/** Shared overview mapping for staff View and the public email/portal document. */
export function overviewFromContractData(contract: any) {
  const data = contract?.contractData ?? {};
  const display = data.display ?? contract?.overview ?? {};
  const proposal = data.proposal ?? {};
  const legal = data.legal ?? {};
  const project = data.project ?? contract?.project ?? {};
  const client = data.client ?? contract?.project?.client ?? {};
  const profile =
    data.organization?.profile ?? contract?.project?.organization?.profile ?? {};
  const license = licenseFromProfile(profile);
  const jobsite =
    jobsiteFromUnknown(project.jobsiteAddress) ??
    jobsiteFromUnknown(contract?.project?.jobsiteAddress);
  const premisesJurisdiction = resolvePremisesJurisdiction({
    municipality:
      text(project.municipalityOfProject) || text(client.municipalityOfProject),
    permitMunicipality: municipalityFromDetailRows(
      collectProjectDetailRows({
        csfBlocks: buildCsfBlocks(data),
        projectDetails: data.projectDetails,
        project,
      }),
    ),
    frozen: display.premisesJurisdiction,
    jobsite,
  });
  const authored = text(legal.overviewParagraph) || text(display.overviewParagraph);
  const timelineObj = asRecord(data.timeline) ?? asRecord(contract?.timeline) ?? {};
  const timelineNarrative =
    narrativeText(data.timeline) || narrativeText(contract?.timeline);

  return {
    preparedDate:
      formatContractDate(display.preparedDate) ||
      formatContractDate(data.transactionDate) ||
      formatContractDate(contract?.createdAt),
    effectiveDate:
      formatContractDate(display.effectiveDate) ||
      formatContractDate(contract?.companySignedAt) ||
      formatContractDate(display.preparedDate) ||
      formatContractDate(contract?.createdAt),
    designConsultant:
      text(display.designConsultant) || text(contract?.creator?.fullName),
    designConsultantEmail:
      text(display.designConsultantEmail) || text(contract?.creator?.email),
    licenseNumber: text(display.licenseNumber) || license.licenseNumber,
    licenseClassification: text(display.licenseClassification) || license.licenseClassification,
    licenseIssuingState: text(display.licenseIssuingState) || license.licenseIssuingState,
    premisesJurisdiction,
    jurisdictionPreamble:
      authored ||
      defaultJurisdictionPreamble({
        contractorName: text(profile?.name),
        premisesJurisdiction,
      }),
    weeksUntilStart:
      text(display.weeksUntilStart) ||
      text(proposal.weekUntilProjectStarts) ||
      text(timelineObj.weeksUntilStart) ||
      text(timelineObj.weekUntilProjectStarts),
    weeksToComplete:
      text(display.weeksToComplete) ||
      text(proposal.weekUntilProjectEnds) ||
      text(timelineObj.weeksToComplete) ||
      text(timelineObj.weekUntilProjectEnds) ||
      timelineNarrative,
    tentativeStartDate:
      formatContractDate(display.tentativeStartDate) ||
      formatContractDate(proposal.tentativeStartDate) ||
      formatContractDate(timelineObj.tentativeStartDate),
    allowances: normalizeAllowances(
      display.allowances ??
        proposal.allowances ??
        project.allowances ??
        contract?.allowances,
    ),
  };
}

const METHOD_LABELS: Record<string, string> = {
  check: 'Check',
  CHECK: 'Check',
  ach: 'ACH',
  ELECTRONIC: 'Electronic (ACH / card)',
  credit_card: 'Credit card',
  wire: 'Wire',
  cash: 'Cash',
};

function paymentMethodList(raw: unknown): string | null {
  if (Array.isArray(raw)) {
    const labels = raw
      .map((id) => METHOD_LABELS[String(id)] || text(id))
      .filter(Boolean);
    return labels.length ? labels.join(', ') : null;
  }
  return text(raw);
}

/** Company remittance + this contract's down-payment method for §VI. */
export function paymentInstructionsFromContract(contract: any) {
  const data = contract?.contractData ?? {};
  const display = asRecord(data.display) ?? {};
  const profile =
    asRecord(data.organization?.profile) ??
    asRecord(contract?.project?.organization?.profile) ??
    asRecord(contract?.company) ??
    {};
  const company = asRecord(contract?.company) ?? {};
  return {
    downPaymentMethod:
      text(display.downPaymentMethod) || text(contract?.paymentMethod),
    checkNumber: text(display.checkNumber) || text(contract?.paymentCheckNumber),
    acceptedMethods:
      paymentMethodList(profile.paymentMethods) ||
      paymentMethodList(company.paymentMethods),
    mailToPayee: text(profile.mailInCheckPayee) || text(company.mailInCheckPayee),
    mailToAddress:
      formatAddress([
        text(profile.mailInCheckStreet) || text(company.mailInCheckStreet),
        [
          text(profile.mailInCheckCity) || text(company.mailInCheckCity),
          text(profile.mailInCheckState) || text(company.mailInCheckState),
          text(profile.mailInCheckPostalCode) || text(company.mailInCheckPostalCode),
        ]
          .filter(Boolean)
          .join(' '),
      ]) || text(profile.mailInCheckInstructions),
    paymentLinkUrl: text(profile.paymentLinkUrl) || text(company.paymentLinkUrl),
    paymentLinkLabel:
      text(profile.paymentLinkLabel) || text(company.paymentLinkLabel) || 'Pay online',
  };
}

function detailValue(value: unknown): string | null {
  if (value == null || value === '') return null;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'string') return text(value);
  if (Array.isArray(value)) {
    const parts = value.map((item) => detailValue(item)).filter(Boolean);
    return parts.length ? parts.join('; ') : null;
  }
  return text(String(value));
}

function mergeDetailRows(
  base: Array<[string, string]>,
  extras: Array<[string, string]>,
): Array<[string, string]> {
  const seen = new Set(base.map(([label]) => label.toLowerCase()));
  const out = [...base];
  for (const [label, value] of extras) {
    const key = label.toLowerCase();
    if (!label || !value || seen.has(key)) continue;
    seen.add(key);
    out.push([label, value]);
  }
  return out;
}

function projectFieldDetailRows(project: Record<string, unknown> | null): Array<[string, string]> {
  if (!project) return [];
  const rows: Array<[string, string]> = [];
  const permit = text(project.permitDocumentation);
  if (permit) rows.push(['Permitting', permit]);
  const permitDue = formatContractDate(project.permitDueDate);
  if (permitDue) rows.push(['Permit due date', permitDue]);
  const renderingType = text(project.renderingType);
  const renderingRequired =
    project.renderingRequired === true ||
    project.renderingRequired === 'true' ||
    project.renderingRequired === 'YES';
  if (renderingType || renderingRequired) {
    rows.push(['Drawings / renderings', renderingType || 'Required']);
  }
  const special = text(project.specialRequests);
  if (special) rows.push(['Special requests', special]);
  const dumpster = text(project.dumpsterLocation);
  if (dumpster) rows.push(['Dumpster', dumpster]);
  const materials = text(project.materialLocation);
  if (materials) rows.push(['Location for materials', materials]);
  const driveway = text(project.drivewayType);
  if (driveway) rows.push(['Driveway', driveway.replace(/_/g, ' ')]);
  const demoDesc = text(project.demolitionDescription);
  const demoExtent = text(project.demolitionExtent);
  const demoRequired =
    project.demolitionRequired === true ||
    project.demolitionRequired === 'true' ||
    project.demolitionRequired === 'YES';
  if (demoDesc) {
    rows.push(['Demolition', demoDesc]);
  } else if (demoExtent && !/^none$/i.test(demoExtent)) {
    rows.push(formatPaperDetailRow('Extent of Demolition', demoExtent));
  } else if (demoRequired) {
    rows.push(['Demolition', 'Required']);
  }
  const sewer = text(project.sewerSystem);
  if (sewer) rows.push(['Type of sewer system', sewer.replace(/_/g, ' ')]);
  const water = text(project.waterSystem);
  if (water) rows.push(['Type of water system', water.replace(/_/g, ' ')]);
  const siteNotes = text(project.siteNotes);
  if (siteNotes) rows.push(['Site notes', siteNotes]);
  return rows;
}

export function assumptionsFromContract(input: unknown): string {
  const root = asRecord(input) ?? {};
  const data = asRecord(root.contractData) ?? {};
  const proposal = asRecord(data.proposal) ?? {};
  const project = asRecord(data.project) ?? asRecord(root.project) ?? {};
  const related = asRecord(root.project) ?? {};
  const fromProposal = text(proposal.assumptionsExclusions);
  if (fromProposal) return fromProposal;
  const fromProject =
    text(project.assumptionsOverride) || text(related.assumptionsOverride);
  if (fromProject) return fromProject;
  for (const row of asArray(data.templates ?? root.templates)) {
    if (!row || typeof row !== 'object') continue;
    const item = row as Record<string, unknown>;
    const type = text(item.type) || '';
    const name = text(item.name) || '';
    if (!/assumption/i.test(type) && !/assumption/i.test(name)) continue;
    const content = text(item.content);
    if (content) return content;
  }
  return '';
}

/** Project-level notes (permits, HOA, dumpster, demolition, …) for II. Project Details. */
export function collectProjectDetailRows(input: {
  csfBlocks: CsfBlock[];
  projectDetails?: unknown;
  project?: unknown;
}): Array<[string, string]> {
  const fromNotes = projectDetailRowsFromBlocks(input.csfBlocks).map(([label, value]) =>
    formatPaperDetailRow(label, value),
  );
  const extras: Array<[string, string]> = [];
  for (const row of asArray(input.projectDetails)) {
    if (!row || typeof row !== 'object') continue;
    const item = row as Record<string, unknown>;
    const label = text(item.label);
    const value = detailValue(item.value);
    if (label && value) extras.push(formatPaperDetailRow(label, value));
  }
  extras.push(
    ...projectFieldDetailRows(asRecord(input.project)).map(([label, value]) =>
      formatPaperDetailRow(label, value),
    ),
  );
  return mergeDetailRows(fromNotes, extras);
}

/** Plain text for React children. Parser handles tags and entities in one pass. */
function stripHtml(value: string): string {
  const withBreaks = value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n');
  const doc = new DOMParser().parseFromString(withBreaks, 'text/html');
  return (doc.body.textContent ?? '').replace(/\u00a0/g, ' ').trim();
}

/** Numbered “what we are building” lines for II. Overview. Never invents text. */
export function overviewItemsFromSummary(raw: unknown): string[] {
  const source = text(raw);
  if (!source) return [];
  const lis = [...source.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)]
    .map((match) => stripHtml(match[1]).replace(/^\d+[.)]\s*/, '').trim())
    .filter(Boolean);
  if (lis.length > 0) return lis;
  return stripHtml(source)
    .split(/\n+/)
    .map((line) => line.replace(/^\d+[.)]\s*/, '').trim())
    .filter(Boolean);
}

export type InvestmentDetailRow = { label: string; amount: number | null };
export type ContractDiscountRow = { description: string; amount: number | null };

/** Priced rollup for VI — element totals and Project Details, not a BOM. */
export function investmentDetailsFromContract(input: unknown): InvestmentDetailRow[] {
  const root = asRecord(input) ?? {};
  const data = asRecord(root.contractData) ?? root;
  const summary =
    asRecord(data.investmentSummary) ?? asRecord(root.investmentSummary) ?? {};
  const rows: InvestmentDetailRow[] = [];
  const projectDetailsTotal = asMoneyNumber(summary.projectDetailsTotal);
  if (projectDetailsTotal != null && projectDetailsTotal > 0) {
    rows.push({ label: 'Project Details', amount: projectDetailsTotal });
  }
  const allowancesTotal =
    asMoneyNumber(summary.allowancesTotal) ??
    sumAllowanceAmounts(
      firstNonEmptyList(
        asRecord(data.display)?.allowances,
        asRecord(data.proposal)?.allowances,
        asRecord(data.project)?.allowances,
        root.allowances,
      ),
    );
  for (const el of asArray(data.scope ?? root.scope)) {
    if (!el || typeof el !== 'object') continue;
    const item = el as Record<string, unknown>;
    if (item.clientIncluded === false) continue;
    const label =
      [text(item.elementTypeDisplayName), text(item.instanceLabel)]
        .filter(Boolean)
        .join(' — ') || text(item.description);
    if (!label) continue;
    const amount = asMoneyNumber(item.sellPrice ?? item.elementTotal ?? item.total);
    if (amount == null) continue;
    rows.push({ label, amount });
  }
  if (allowancesTotal != null && allowancesTotal > 0) {
    rows.push({ label: 'Allowances', amount: allowancesTotal });
  }
  return rows;
}

export function discountsFromContract(input: unknown): ContractDiscountRow[] {
  const root = asRecord(input) ?? {};
  const data = asRecord(root.contractData) ?? root;
  const summary =
    asRecord(data.investmentSummary) ?? asRecord(root.investmentSummary) ?? {};
  const source = firstNonEmptyList(data.discounts, root.discounts, summary.discounts);
  const rows: ContractDiscountRow[] = [];
  for (const row of source) {
    if (!row || typeof row !== 'object') continue;
    const item = row as Record<string, unknown>;
    if (item.isApplied === false) continue;
    const description = text(item.description) || text(item.label) || 'Discount';
    const amount = asMoneyNumber(item.amount);
    if (amount == null || amount === 0) continue;
    rows.push({ description, amount: Math.abs(amount) });
  }
  return rows;
}

export const TIMELINE_DISCLAIMER =
  '*The dates above are best estimates given the present construction environment and are subject to all conditions outlined in the contract which may impact the completion schedule, including weather delays and material availability.';

export function applyOverviewToDocumentProps(
  props: ContractDocumentViewProps,
  overview: ReturnType<typeof overviewFromContractData>,
): ContractDocumentViewProps {
  return {
    ...props,
    preparedDate: overview.preparedDate,
    effectiveDate: overview.effectiveDate,
    designConsultant: overview.designConsultant,
    designConsultantEmail: overview.designConsultantEmail,
    licenseNumber: overview.licenseNumber,
    licenseClassification: overview.licenseClassification,
    licenseIssuingState: overview.licenseIssuingState,
    premisesJurisdiction: overview.premisesJurisdiction,
    jurisdictionPreamble: overview.jurisdictionPreamble,
    timeline: {
      weeksUntilStart: overview.weeksUntilStart,
      weeksToComplete: overview.weeksToComplete,
      tentativeStartDate: overview.tentativeStartDate,
    },
    allowances: overview.allowances,
  };
}
